import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { SyncService } from './sync.service';

type SyncMsg =
  | 'startSync'
  | 'startDefSync'
  | 'initDefCount'
  | 'downloadDefComplete'
  | 'uploadPulseComplete'
  | 'pulseSyncFailed' 
  | 'assessmentFilesComplete'
  | 'startAssessmentsSync'
  | 'sentAssessmentsComplete' 
  | 'pushAssessmentsPartial'
  | 'syncAssessmentsComplete'
  | 'startSingleSync'
  | 'singleSyncComplete'
  | 'error';

export type SyncUiState = {
  show: boolean;
  label: string;
  pct: number;
  done: boolean;
  error: string;
};

const SYNC_STEPS: Partial<Record<SyncMsg, { label: string; pct: number }>> = {
  startSync: { label: 'Starting data sync', pct: 0 },
  startDefSync: { label: 'Checking form definitions', pct: 10 },
  //initDefCount: { label: 'Storing definitions…', pct: 30 },
  downloadDefComplete: { label: 'Definitions synced ✅', pct: 45 },
  uploadPulseComplete: { label: 'Pulse data sent ✅', pct: 55 },
  pulseSyncFailed: { label: 'Pulse sync failed (will retry later)', pct: 55 },
  assessmentFilesComplete: { label: 'Assessment Files Sent', pct: 60 },
  startAssessmentsSync: { label: 'Syncing assessments…', pct: 65 },
  sentAssessmentsComplete: { label: 'Sent assessments ✅', pct: 80 },
  pushAssessmentsPartial: { label: 'Some assessments failed to send (will retry)', pct: 80 },
  syncAssessmentsComplete: { label: 'Downloaded latest assessments ✅', pct: 100 },
  startSingleSync: { label: 'Syncing this assessment…', pct: 20 },
  singleSyncComplete: { label: 'Assessment synced ✅', pct: 100 },

};

@Injectable({ providedIn: 'root' })
export class SyncUiService {
  private readonly state$ = new BehaviorSubject<SyncUiState>({
    show: false,
    label: '',
    pct: 0,
    done: false,
    error: '',
  });

  // expose as observable for templates
  readonly ui$ = this.state$.asObservable();

  // throttle spammy updates (optional)
  private lastUpdateAt = 0;
  private readonly minIntervalMs = 250;

  constructor(private sync: SyncService) {
    // Subscribe ONCE for the whole app lifetime
    this.sync.progress$.subscribe((event: any) => this.onProgress(event));
  }

  private onProgress(event: any) {
    const msg = event?.msg as SyncMsg;
    if (!msg) return;

    if (msg === 'error') {
        // console.log("🚀 ~ sync-ui.service.ts:75 ~ SyncUiService ~ onProgress ~ event:", event)
      this.state$.next({
        show: true,
        label: event?.errorMessage ?? 'Sync failed. Please try again.',
        pct: this.state$.value.pct,
        done: false,
        error: event?.errorMessage ?? 'Sync failed. Please try again.',
      });
      return;
    }

    const step = SYNC_STEPS[msg];
    if (!step) return;

    // throttle so UI doesn’t flicker if lots of events
    const now = Date.now();
    if (now - this.lastUpdateAt < this.minIntervalMs && msg !== 'syncAssessmentsComplete') return;
    this.lastUpdateAt = now;

    const pctFromEvent =
  typeof event?.pct === 'number' ? event.pct : undefined;

  // fall back to your mapping if event doesn’t provide pct
  const pct =
    pctFromEvent ?? step?.pct ?? this.state$.value.pct;

  const done = pct >= 100;
    

    this.state$.next({
      show: true,
      label: step.label,
      pct: step.pct,
      done,
      error: '',
    });

    if (done) {
      // small pause so user sees "100%" then hide
      setTimeout(() => this.reset(), 1000);
    }
  }

  hide() {
    this.state$.next({ ...this.state$.value, show: false });
  }

  reset() {
    this.state$.next({
      show: false,
      label: '',
      pct: 0,
      done: false,
      error: '',
    });
  }
}
